import { DataTable } from 'mantine-datatable';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Button,
  Center,
  Container,
  Group,
  Paper,
  SimpleGrid,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { dateToDateHourString } from '@utils/date';
import {
  mapBookingStatus,
  mapHolidayVariationStatus,
  mapPaymentStatus,
} from '@utils/mappers';

import { CustomerBookingsRequestParams } from '@interfaces/booking.interface';

import {
  useCountCustomerBookingsQuery,
  useFindManyCustomerBookingsQuery,
} from '@api/bookings.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';

import BookingDetail from './BookingDetail';
import PaginationRow from './PaginationRow';
import { areParticipantsIncomplete, getBookingTotal } from '@utils/booking';

export default function BookingsList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [params, setParams] = useQueryParams();
  const idParam = useMemo(() => params.get('id'), [params]);
  const { t } = useTranslation();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<CustomerBookingsRequestParams>({
    page: 1,
    pageLength: 10,
  });

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data = [], isLoading } = useFindManyCustomerBookingsQuery(filters);

  const {
    data: customerBookingsCount = { count: 0 },
    isLoading: isLoadingCount,
  } = useCountCustomerBookingsQuery();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const title = useMemo(
    () =>
      `${t('bookings.list.title')} ${!isLoadingCount ? `(${customerBookingsCount.count})` : ''}`,
    [customerBookingsCount.count, isLoadingCount, t],
  );

  const handleOpenDetails = async (id: number) => {
    const newParams = new URLSearchParams(params);
    newParams.set('id', id.toString());
    setParams(newParams);
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const totalPages = Math.ceil(
    customerBookingsCount.count / filters.pageLength!,
  );

  return (
    <Container mt={{ base: 'md', xs: 0 }}>
      {idParam ? (
        <BookingDetail />
      ) : (
        <>
          <Title mb="md" order={2}>
            {title}
          </Title>

          <SimpleGrid cols={{ base: 1, sm: 2 }} hiddenFrom="md">
            {data.length === 0 ? (
              <Center>
                <Text fz="sm">{t('bookings.list.noRecords')}</Text>
              </Center>
            ) : (
              data.map((record) => {
                const totalPrice = getBookingTotal(record);

                const mappedPaymentStatus = mapPaymentStatus(
                  record.totalPaid ?? 0,
                  totalPrice,
                  // Use paid status if there is at least one paid payment
                  record.payments.some(
                    (payment) => payment.paymentStatus === 'paid',
                  )
                    ? 'paid'
                    : // Otherwise check the last payment status
                      (record.payments.at(-1)?.paymentStatus ?? 'pending'), // We are assuming they are ordered by createdAt ASC
                );

                return (
                  <Paper key={record.id} withBorder p="md">
                    <Group>
                      {areParticipantsIncomplete(record) && (
                        <Tooltip
                          label={t('bookings.list.tooltips.incompleteData')}
                        >
                          <IconAlertCircle color="#e01818" />
                        </Tooltip>
                      )}
                      <Text fw="bold" size="lg">
                        {record.holidayName}
                      </Text>
                    </Group>
                    <Group mt="md">
                      <Text>{t('bookings.list.columns.bookingDate')}</Text>
                      <Text>
                        {dateToDateHourString(new Date(record.createdAt))}
                      </Text>
                    </Group>
                    <Group mt="xs">
                      <Text>{t('bookings.list.columns.bookingStatus')}</Text>
                      <Tooltip
                        label={mapBookingStatus(record.bookingStatus).tooltip}
                      >
                        <Badge
                          size="sm"
                          color={mapBookingStatus(record.bookingStatus).color}
                        >
                          {mapBookingStatus(record.bookingStatus).label}
                        </Badge>
                      </Tooltip>
                    </Group>
                    <Group mt="xs">
                      <Text>{t('bookings.list.columns.holidayStatus')}</Text>
                      <Tooltip
                        label={
                          mapHolidayVariationStatus(
                            record.holidayVariation?.status ?? null,
                          ).tooltip
                        }
                      >
                        <Badge
                          size="sm"
                          color={
                            mapHolidayVariationStatus(
                              record.holidayVariation?.status ?? null,
                            ).color
                          }
                        >
                          {
                            mapHolidayVariationStatus(
                              record.holidayVariation?.status ?? null,
                            ).label
                          }
                        </Badge>
                      </Tooltip>
                    </Group>

                    <Group mt="xs">
                      <Text>{t('bookings.list.columns.paymentStatus')}</Text>
                      <Tooltip label={mappedPaymentStatus.tooltip}>
                        <Badge size="sm" color={mappedPaymentStatus.color}>
                          {mappedPaymentStatus.label}
                        </Badge>
                      </Tooltip>
                    </Group>

                    <Button
                      fullWidth
                      mt="md"
                      onClick={() => handleOpenDetails(record.id)}
                    >
                      {t('bookings.list.columns.details')}
                    </Button>
                  </Paper>
                );
              })
            )}
          </SimpleGrid>

          <DataTable
            visibleFrom="md"
            striped
            noRecordsText={t('bookings.list.noRecords')}
            minHeight={data.length === 0 ? 100 : undefined}
            fetching={isLoading || isLoadingCount}
            columns={[
              {
                accessor: '',
                title: '',
                render: (record) =>
                  areParticipantsIncomplete(record) && (
                    <Center inline>
                      <Tooltip
                        label={t('bookings.list.tooltips.incompleteData')}
                      >
                        <IconAlertCircle color="#e01818" />
                      </Tooltip>
                    </Center>
                  ),
              },
              {
                accessor: 'holidayName',
                title: t('bookings.list.columns.holiday'),
                width: '200px',
                render: (record) => (
                  <Center inline>
                    <Text fz="sm">{record.holidayName}</Text>
                  </Center>
                ),
              },
              {
                accessor: 'createdAt',
                title: t('bookings.list.columns.bookingDate'),
                render: (record) => (
                  <Center inline>
                    <Text fz="sm">
                      {dateToDateHourString(new Date(record.createdAt))}
                    </Text>
                  </Center>
                ),
              },
              {
                accessor: 'bookingStatus',
                title: t('bookings.list.columns.bookingStatus'),
                textAlign: 'center',
                render: (record) => (
                  <Center inline>
                    <Tooltip
                      label={mapBookingStatus(record.bookingStatus).tooltip}
                    >
                      <Badge
                        size="sm"
                        color={mapBookingStatus(record.bookingStatus).color}
                      >
                        {mapBookingStatus(record.bookingStatus).label}
                      </Badge>
                    </Tooltip>
                  </Center>
                ),
              },
              {
                accessor: 'booking.holidayVariation.status',
                title: t('bookings.list.columns.holidayStatus'),
                textAlign: 'center',
                render: (record) => (
                  <Center inline>
                    <Tooltip
                      label={
                        mapHolidayVariationStatus(
                          record.holidayVariation?.status ?? null,
                        ).tooltip
                      }
                    >
                      <Badge
                        size="sm"
                        color={
                          mapHolidayVariationStatus(
                            record.holidayVariation?.status ?? null,
                          ).color
                        }
                      >
                        {
                          mapHolidayVariationStatus(
                            record.holidayVariation?.status ?? null,
                          ).label
                        }
                      </Badge>
                    </Tooltip>
                  </Center>
                ),
              },
              {
                accessor: 'booking.lastPaymentStatus',
                title: t('bookings.list.columns.paymentStatus'),
                textAlign: 'center',
                render: (record) => {
                  const totalPrice = getBookingTotal(record);

                  const mappedPaymentStatus = mapPaymentStatus(
                    record.totalPaid ?? 0,
                    totalPrice,
                    // Use paid status if there is at least one paid payment
                    record.payments.some(
                      (payment) => payment.paymentStatus === 'paid',
                    )
                      ? 'paid'
                      : // Otherwise check the last payment status
                        (record.payments.at(-1)?.paymentStatus ?? 'pending'), // We are assuming they are ordered by createdAt ASC
                  );

                  return (
                    <Center inline>
                      <Tooltip label={mappedPaymentStatus.tooltip}>
                        <Badge size="sm" color={mappedPaymentStatus.color}>
                          {mappedPaymentStatus.label}
                        </Badge>
                      </Tooltip>
                    </Center>
                  );
                },
              },
              {
                accessor: 'actions',
                title: '',
                width: '0%',
                render: (record) => {
                  return (
                    <Center inline>
                      <Tooltip label={t('bookings.list.tooltips.viewDetails')}>
                        <Button
                          size="xs"
                          onClick={() => handleOpenDetails(record.id)}
                        >
                          {t('bookings.list.columns.details')}
                        </Button>
                      </Tooltip>
                    </Center>
                  );
                },
              },
            ]}
            records={data}
          />
          {data.length > 0 && (
            <PaginationRow
              page={filters.page!}
              pageLength={filters.pageLength!}
              totalPages={totalPages}
              onPageChange={(newPage) =>
                setFilters({ ...filters, page: newPage })
              }
              onPageLengthChange={(newPageLength) =>
                setFilters({ pageLength: newPageLength, page: 1 })
              }
            />
          )}
        </>
      )}
    </Container>
  );
}
